import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Send, Phone, Video, Search, ArrowLeft, Bot, Sparkles, Loader2, Languages } from 'lucide-react';
import { toast } from 'sonner';

// Renders a message bubble that can toggle between translated + original text
function MessageBubble({ m, isMe, t }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const hasTranslation = !!(m.translated_content && m.translation_lang && m.source_lang && m.translation_lang !== m.source_lang);
  // For the sender, show their own original. For the receiver, show the translation (if available).
  const displayText = showOriginal
    ? (m.original_content || m.content)
    : (isMe ? (m.original_content || m.content) : (m.translated_content || m.content));
  const showTranslatedBy = hasTranslation && !isMe && !showOriginal;
  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[70%] px-3 py-2 text-sm ${isMe ? 'bg-obsidian text-white' : 'bg-mist text-foreground'}`}>
        {m.is_ai_reply && (
          <div className={`flex items-center gap-1 text-[9px] mb-1 ${isMe ? 'text-gold' : 'text-gold'}`}>
            <Bot size={10} /> Auto-reply
          </div>
        )}
        {showTranslatedBy && (
          <div className="flex items-center gap-1 text-[9px] mb-1 text-amber-600 font-medium">
            <Languages size={10} />
            <span>{t('messages.translated_from', { lang: (m.source_lang || '').toUpperCase() }) !== 'messages.translated_from' ? t('messages.translated_from', { lang: (m.source_lang || '').toUpperCase() }) : `Translated from ${(m.source_lang || '').toUpperCase()}`}</span>
          </div>
        )}
        <p className="whitespace-pre-wrap break-words">{displayText}</p>
        {hasTranslation && !isMe && (
          <button
            onClick={() => setShowOriginal(v => !v)}
            data-testid={`msg-${m.id}-toggle`}
            className="mt-1 text-[9px] underline text-amber-600 hover:text-amber-700"
          >
            {showOriginal
              ? (t('messages.show_translation') !== 'messages.show_translation' ? t('messages.show_translation') : 'Show translation')
              : (t('messages.show_original') !== 'messages.show_original' ? t('messages.show_original') : 'Show original')}
          </button>
        )}
        <p className={`text-[9px] mt-1 ${isMe ? 'text-white/40' : 'text-muted-foreground'}`}>
          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { t, i18n } = useTranslation();
  const { partnerId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [audioCallActive, setAudioCallActive] = useState(false);
  const [aiSettings, setAiSettings] = useState({ ai_reply_mode: 'off' });
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => { loadConversations(); loadSettings(); }, []);
  useEffect(() => { if (partnerId) loadMessages(partnerId); }, [partnerId]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  
  // Update activity status periodically
  useEffect(() => {
    const updateActivity = () => api.post('/settings/update-activity').catch(() => {});
    updateActivity();
    const interval = setInterval(updateActivity, 60000); // every minute
    return () => clearInterval(interval);
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/settings');
      setAiSettings(res.data);
    } catch {}
  };

  const loadConversations = async () => {
    try { const res = await api.get('/messages/conversations'); setConversations(res.data); }
    catch {}
  };

  const loadMessages = async (pid) => {
    try {
      const res = await api.get(`/messages/${pid}`);
      setMessages(res.data);
      const partnerRes = await api.get(`/users/${pid}`);
      setSelectedPartner(partnerRes.data);
    } catch { toast.error('Failed to load messages'); }
  };

  const sendMessage = async () => {
    if (!text.trim() || !partnerId) return;
    try {
      const sourceLang = (i18n?.language || 'en').split('-')[0];
      const res = await api.post('/messages', { receiver_id: partnerId, content: text.trim(), source_lang: sourceLang });
      setText('');
      setSuggestions([]);
      loadMessages(partnerId);
      loadConversations();
      if (res.data.ai_auto_reply_sent) {
        toast.info('An AI auto-reply was sent on behalf of the recipient');
      }
    } catch { toast.error('Failed to send'); }
  };

  const getAiSuggestions = async () => {
    if (!partnerId || messages.length === 0) {
      toast.error('Need some conversation history for suggestions');
      return;
    }
    try {
      setLoadingSuggestions(true);
      const res = await api.post('/messages/suggest-reply', { partner_id: partnerId, context_messages: 5 });
      setSuggestions(res.data.suggestions || []);
      if (res.data.suggestions?.length === 0) {
        toast.info('No suggestions available');
      }
    } catch (err) {
      toast.error('Failed to get suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setText(suggestion);
    setSuggestions([]);
  };

  const searchUsers = async () => {
    if (!search.trim()) return;
    try { const res = await api.get(`/users?q=${search}`); setUsers(res.data.filter(u => u.id !== user?.id)); }
    catch {}
  };

  const startAudioCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioRef.current = stream;
      setAudioCallActive(true);
      toast.success('Audio call started (free)');
    } catch { toast.error('Microphone access denied'); }
  };

  const endAudioCall = () => {
    audioRef.current?.getTracks().forEach(t => t.stop());
    setAudioCallActive(false);
    toast.success('Call ended');
  };

  const startVideoCall = async () => {
    try {
      const res = await api.post('/payments/checkout', { call_type: 'video', origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch { toast.error('Video call requires payment'); }
  };

  const selectConversation = (pid) => navigate(`/messages/${pid}`);

  return (
    <div className="animate-in flex h-[calc(100vh-7rem)]" data-testid="messages-page">
      {/* Sidebar */}
      <div className={`${partnerId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 border-r border-border/40 shrink-0`}>
        <div className="p-3 border-b border-border/40">
          <div className="flex gap-2">
            <Input
              data-testid="message-search"
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchUsers()}
              className="rounded-sm text-xs h-8"
            />
            <Button data-testid="message-search-btn" variant="outline" size="sm" className="rounded-sm h-8 px-3" onClick={searchUsers}>
              <Search size={12} />
            </Button>
          </div>
        </div>

        {/* Search results */}
        {users.length > 0 && (
          <div className="border-b border-border/40 p-2 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-2">Search Results</p>
            {users.map(u => (
              <button key={u.id} onClick={() => { setUsers([]); setSearch(''); navigate(`/messages/${u.id}`); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-mist rounded-sm text-left" data-testid={`search-user-${u.id}`}>
                <Avatar className="h-6 w-6 border"><AvatarFallback className="bg-obsidian text-white text-[8px]">{(u.display_name || 'U')[0]}</AvatarFallback></Avatar>
                <span className="text-xs">{u.display_name || u.username}</span>
              </button>
            ))}
          </div>
        )}

        {/* Conversations */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-0.5">
            {conversations.map(c => (
              <button
                key={c.partner_id}
                onClick={() => selectConversation(c.partner_id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition-colors ${partnerId === c.partner_id ? 'bg-obsidian text-white' : 'hover:bg-mist'}`}
                data-testid={`conv-${c.partner_id}`}
              >
                <Avatar className="h-8 w-8 border shrink-0">
                  <AvatarFallback className={`text-[10px] font-sans ${partnerId === c.partner_id ? 'bg-gold text-obsidian' : 'bg-obsidian text-white'}`}>
                    {(c.partner?.display_name || 'U')[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{c.partner?.display_name || 'User'}</p>
                  <p className={`text-[10px] truncate ${partnerId === c.partner_id ? 'text-white/60' : 'text-muted-foreground'}`}>{c.last_message?.content}</p>
                </div>
                {c.unread_count > 0 && <Badge className="bg-gold text-obsidian rounded-full text-[9px] h-4 px-1.5">{c.unread_count}</Badge>}
              </button>
            ))}
            {conversations.length === 0 && <p className="text-center text-xs text-muted-foreground py-8">No conversations yet. Search for a user to start chatting.</p>}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`${partnerId ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {partnerId && selectedPartner ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-3 border-b border-border/40">
              <div className="flex items-center gap-3">
                <button className="md:hidden" onClick={() => navigate('/messages')} data-testid="back-to-conversations"><ArrowLeft size={16} /></button>
                <Avatar className="h-8 w-8 border"><AvatarFallback className="bg-obsidian text-white text-[10px]">{(selectedPartner.display_name || 'U')[0]}</AvatarFallback></Avatar>
                <span className="text-sm font-medium">{selectedPartner.display_name}</span>
              </div>
              <div className="flex items-center gap-2">
                {audioCallActive ? (
                  <Button data-testid="end-audio-call" size="sm" variant="destructive" className="rounded-sm text-xs h-8" onClick={endAudioCall}>
                    <Phone size={12} className="mr-1" /> End Call
                  </Button>
                ) : (
                  <Button data-testid="start-audio-call" variant="outline" size="sm" className="rounded-sm text-xs h-8" onClick={startAudioCall}>
                    <Phone size={12} className="mr-1" /> Audio
                  </Button>
                )}
                <Button data-testid="start-video-call" variant="outline" size="sm" className="rounded-sm text-xs h-8 border-gold text-gold hover:bg-gold hover:text-obsidian" onClick={startVideoCall}>
                  <Video size={12} className="mr-1" /> Video
                </Button>
              </div>
            </div>

            {/* Audio call indicator */}
            {audioCallActive && (
              <div className="bg-obsidian text-white px-4 py-2 flex items-center gap-2 text-xs">
                <div className="w-2 h-2 bg-green-400 rounded-full voice-active" />
                Audio call in progress (free)
              </div>
            )}

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {messages.map(m => {
                  const isMe = m.sender_id === user?.id;
                  return <MessageBubble key={m.id} m={m} isMe={isMe} t={t} />;
                })}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            {/* AI Suggestions */}
            {suggestions.length > 0 && (
              <div className="px-3 py-2 border-t border-border/40 bg-gold/5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={12} className="text-gold" />
                  <span className="text-[10px] font-medium text-gold">AI Suggestions</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => selectSuggestion(s)}
                      className="text-xs px-3 py-1.5 bg-white border border-border/40 rounded-full hover:border-gold hover:bg-gold/10 transition-colors"
                      data-testid={`suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-border/40">
              <div className="flex gap-2">
                {aiSettings.ai_reply_mode === 'suggestions' && (
                  <Button
                    data-testid="ai-suggest-btn"
                    variant="outline"
                    onClick={getAiSuggestions}
                    disabled={loadingSuggestions || messages.length === 0}
                    className="rounded-sm h-10 px-3 border-gold text-gold hover:bg-gold hover:text-obsidian shrink-0"
                    title="Get AI reply suggestions"
                  >
                    {loadingSuggestions ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
                  </Button>
                )}
                <Input
                  data-testid="message-input"
                  placeholder="Type a message..."
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className="rounded-sm text-sm h-10"
                />
                <Button data-testid="message-send-btn" onClick={sendMessage} disabled={!text.trim()} className="rounded-sm h-10 px-4 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
                  <Send size={14} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Select a conversation or search for a user to start messaging.
          </div>
        )}
      </div>
    </div>
  );
}
